import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import api from "../../api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import crossIcon from "../../assets/icons/crossIcon.png";
import fileIcon from "../../assets/icons/sboxfile.png";
import linkIcon from "../../assets/icons/sboxlink.png";
import { BsArrowRepeat } from "react-icons/bs";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
// import { formatDateToUserTimeZone } from "../../utils/formatDate";
import axios from "axios";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";

const TrainingWebsite = ({ uploadedWebsites = [] }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("");
  const [uploadedSites, setUploadedSites] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(null);

  // 🆕 summary states
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // ✅ Summary state
  const [summary, setSummary] = useState({
    totalChars: 0,
    limit: 100000,
    usedPercent: 0,
    isOverLimit: false,
    overByChars: 0,
    breakdown: [],
  });

  const limit = 1000000;

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  // ---------- Utility helpers ----------
  const parseChars = (val) => {
    // Convert numbers represented as strings (maybe with commas) into a safe number
    if (val == null) return 0;
    if (typeof val === "number") return Number.isFinite(val) ? val : 0;
    const cleaned = String(val).replace(/,/g, "").trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const computeSummary = (sites) => {

    if (!sites || sites.length === 0) {
      return {
        totalChars: 0,
        limit,
        usedPercent: 0,
        isOverLimit: false,
        overByChars: 0,
        breakdown: [],
      };
    }

    // ✅ count only approved / active
    const activeSites = sites.filter((s) => s.status === "approved");

    if (activeSites.length === 0) {
      return {
        totalChars: 0,
        limit,
        usedPercent: 0,
        isOverLimit: false,
        overByChars: 0,
        breakdown: [],
      };
    }

    // 🔢 total chars
    const totalChars = activeSites.reduce(
      (sum, site) => sum + parseChars(site.characters),
      0,
    );

    // 📊 percent
    const rawPercent = (totalChars / limit) * 100;
    const usedPercent = Math.min(Number(rawPercent.toFixed(1)), 100);

    // 🚨 limit checks
    const isOverLimit = totalChars > limit;
    const overByChars = isOverLimit ? totalChars - limit : 0;

    // 🧩 breakdown
    const typeMap = {};

    activeSites.forEach((site) => {
      const url = (site.url || "").toLowerCase();
      let type = "other";

      if (url.startsWith("http://") || url.startsWith("https://"))
        type = "http";
      else if (url.startsWith("ftp://")) type = "ftp";
      else if (url.includes("/api")) type = "api";
      else if (url.match(/\.(csv|xls|xlsx)$/)) type = "csv/excel file";
      else if (url.startsWith("urn:")) type = "urn";
      else if (url.includes("file")) type = "file URL";

      const chars = parseChars(site.characters);
      typeMap[type] = (typeMap[type] || 0) + chars;
    });

    const breakdown = Object.entries(typeMap)
      .map(([type, chars]) => ({
        type,
        chars,
        value: totalChars === 0 ? 0 : Math.round((chars / totalChars) * 100),
      }))
      .sort((a, b) => b.value - a.value);

    return {
      totalChars,
      limit,
      usedPercent,
      isOverLimit,
      overByChars,
      breakdown,
    };
  };

  // ✅ New function: handle status change (approved / rejected / pending)
  const handleChangeStatus = async (fileId, newStatus) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/approve-or-reject`,
        { file_id: fileId, approval_status: newStatus, notes: "" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showSuccessToast(`Status updated to ${newStatus}`);
      fetchWebsites();
    } catch (err) {
      console.error("❌ Error updating status:", err);
      showErrorToast("Failed to update status");
    }
  };

  // ---------- API interactions ----------

  // 🆕 Fetch summary after upload
  const fetchSummaryFromBackend = async (uploadIds) => {
    try {
      const idsQuery = uploadIds.join(",");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/summary?ids=${idsQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSummaryDataList(res.data);
      setShowSummaryModal(true);
    } catch (err) {
      console.error("Failed to fetch summaries:", err);
      showErrorToast("Failed to fetch summaries.");
    }
  };

  // 🆕 Regenerate specific summary
  const regenerateSummary = async (fileId, index) => {
    try {
      setLoadingIndex(index); // 🚫 disable button for this index

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/regenerate-summary`,
        { file_id: fileId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const newSummary = res.data.summary;

      setSummaryDataList((prevList) => {
        const updatedList = [...prevList];
        updatedList[index] = {
          ...updatedList[index],
          summary: newSummary,
        };
        return updatedList;
      });

      showSuccessToast("Summary regenerated!");
    } catch (err) {
      console.error("Failed to regenerate summary:", err);
      showErrorToast("Regeneration failed.");
    } finally {
      setLoadingIndex(null); // ✅ re-enable button
    }
  };

  const handleUploadWebsite = async () => {
    if (!url.trim()) {
      setMessage("Please enter a valid Web URL.");
      setMessageType("danger");
      return;
    }

    setLoading(true);
    setMessage(null);

    const payload = {
      urls: [url.trim()],
      approval_notes: "Uploaded from website",
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/newUpload_webpage`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );

      const data = response.data;

      // ==========================
      // 🎯 HANDLE DUPLICATE ONLY
      // ==========================
      if (data.status === "duplicate") {
        showErrorToast("This webpage is already uploaded.");
        setMessage("This webpage already exists.");
        setMessageType("warning");
        return;
      }

      // ==========================
      // 🎯 SUCCESS OR PARTIAL SUCCESS
      // ==========================
      showSuccessToast("Web URL(s) uploaded successfully.");

      if (data.status === "partial") {
        setMessage(
          `Uploaded new URL(s). Skipped duplicates: ${data.duplicate_urls.join(
            ", ",
          )}`,
        );
        setMessageType("warning");
      } else {
        setMessage("Web URL(s) uploaded successfully.");
        setMessageType("success");
      }

      setUrl("");
      fetchWebsites();

      // SUMMARY FETCHING
      // const uploadedItems = data.uploaded_files || [];
      // const uploadIds = uploadedItems.map((item) => item.python_file_uuid);
      // if (uploadIds.length > 0) {
      //   await fetchSummaryFromBackend(uploadIds);
      // }
    } catch (err) {
      console.error("❌ Web URLs upload failed:", err?.response?.data || err);

      if (err.response?.status === 409) {
        // setMessage("This Web URL(s) is already uploaded.");
        // setMessageType("warning");
        showErrorToast(
          "This Web URL(s) has already been uploaded. Ask questions.",
        );
        return;
      }

      // setMessage("Web URLs upload failed.");
      // setMessageType("danger");
      showErrorToast("The Web URL upload failed.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fetch websites ----------
  useEffect(() => {
    // If parent passed an initial uploadedWebsites prop, use it
    if (uploadedWebsites && uploadedWebsites.length > 0) {
      setUploadedSites(uploadedWebsites);
    }
    // initial fetch
    fetchWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWebsites = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/websites`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Be resilient to various response shapes
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.uploaded_sites || res.data?.data || [];

      setUploadedSites(data);

      // 🆕 compute dynamic summary based on fetched data
      const computed = computeSummary(data);
      setSummary(computed);
    } catch (err) {
      console.error("❌ Error fetching uploaded websites:", err);
    }
  };

  const convertToValidDate = (dateStr) => {
    // Input: 27/11/2025, 2:30:44 pm
    const [date, time] = dateStr.split(", ");
    const [day, month, year] = date.split("/");

    // Convert time to 24-hour
    let [t, modifier] = time.split(" ");
    let [h, m, s] = t.split(":");

    h = Number(h);
    if (modifier.toLowerCase() === "pm" && h !== 12) h += 12;
    if (modifier.toLowerCase() === "am" && h === 12) h = 0;

    return `${year}-${month}-${day} ${String(h).padStart(2, "0")}:${m}:${s}`;
  };

  // Recompute summary whenever uploadedSites changes (covers manual edits / deletes)
  useEffect(() => {
    setSummary((prev) => {
      const computed = computeSummary(uploadedSites);
      // Avoid unnecessary state updates if identical
      if (
        computed.totalChars === prev.totalChars &&
        computed.completion === prev.completion &&
        computed.breakdown.length === prev.breakdown.length
      ) {
        return prev;
      }
      return computed;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedSites, limit]);

  // ✅ Utility to detect and render clickable links (without "URL:" prefix)
  const renderWithLinks = (text) => {
    if (!text) return null;

    // Remove "URL:" prefix if exists
    const cleanText = text.replace(/^URL:\s*/i, "");

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return cleanText.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
          style={{ color: "#0d6efd", fontWeight: 500 }}
        >
          {part}
        </a>
      ) : (
        part
      ),
    );
  };

  const { sortedData, requestSort, sortConfig } = useTableSort(uploadedSites);

  return (
    <main
      className="container-fluid mt-2 h-100"
      style={{ paddingBottom: "800px" }}
    >
      <div className="row">
        {/* Left Panel */}
        <div className="col-lg-8 mb-4">
          {/* URL Section */}
          <div
            className="shadow-sm p-4 rounded-lg border"
            // style={{ borderColor: "#e6e7ea" }}
          >
            <h6 className="fw-semibold ak-text-lg">Web URLs</h6>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUploadWebsite();
              }}
            >
              <div className="mb-3">
                <label className="form-label">Add URL</label>
                <div
                  className="p-2 rounded"
                  style={
                    {
                      // backgroundColor: "#FAF9F9",
                      // border: "1px solid #3e3e3e",
                    }
                  }
                >
                  <input
                    type="url"
                    className="form-control"
                    // style={{ backgroundColor: "transparent" }}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/your-link"
                    required
                    disabled={loading} // ✅ input locked while fetching
                  />
                </div>
              </div>
              <button
                type="submit"
                className="custom-btn w-100 fw-semibold "
                // style={{ backgroundColor: "#3e3e3e" }}
                disabled={loading}
              >
                {loading ? "Uploading..." : "Upload"}
              </button>

              {/* {message && (
                <div className={`alert mt-3 alert-${messageType}`}>
                  {message}
                </div>
              )} */}
            </form>
          </div>

          {/* Table */}
          <div className="mt-4">
            <div
              className="table-responsive"
              style={{ maxHeight: "330px", overflowY: "auto" }}
            >
              <table className="table text-left trainingUrls-table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <SortableHeader
                      column="url"
                      label="Web URL's"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      column="characters"
                      label="Total Tokens"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      column="updatedDate"
                      label="Uploaded On"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                    <SortableHeader
                      column="status"
                      label="Status"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sortedData.length > 0 ? (
                    sortedData.map((item, index) => (
                      <tr key={index}>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: "300px" }}
                          title={item.url}
                        >
                          {/* ✅ clickable link render */}
                          {renderWithLinks(item.url)}
                        </td>
                        <td>{item.characters}</td>

                        <td>{formatDateToUserTimeZone(item.updatedDate)}</td>
                        {/* <td>
                          <button className="btn btn-sm btn-outline-dark">
                            <i className="bi bi-eye" />
                          </button>
                        </td> */}
                        <td className="text-left">
                          <select
                            className={`badge ${
                              item.status === "approved"
                                ? "bg-success"
                                : item.status === "pending"
                                  ? "bg-warning"
                                  : "bg-danger "
                            }`}
                            value={item.status}
                            onChange={(e) =>
                              handleChangeStatus(item.id, e.target.value)
                            }
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="refresh-prompts ak-text-md">
                        No websites have been trained yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Panel: Summary */}
        <div className="col-lg-4">
          <div className="card profile p-4 shadow-sm">
            <h5 className="fw-semibold mb-4 ak-text-md">Summary</h5>

            {/* Circular Progress */}
            <div className="d-flex justify-content-center align-items-center mb-4 position-relative">
              <svg
                width="140"
                height="140"
                viewBox="0 0 36 36"
                className="circular-chart"
              >
                <path
                  className="circle-bg"
                  d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#eee"
                  strokeWidth="2"
                />
                <path
                  className="circle"
                  strokeDasharray={`${summary.usedPercent}, 100`}
                  stroke={summary.isOverLimit ? "#dc3545" : "#3e3e3e"}
                  d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  strokeWidth="2"
                />
              </svg>
              <div className="position-absolute text-center">
                <div className="ak-text-md">
                  {summary.isOverLimit ? "Limit Exceeded" : "Used"}
                </div>
                <div className="fw-bold fs-2">{summary.usedPercent}%</div>
              </div>
            </div>

            {/* Characters and Limit */}
            <div className="row text-center mb-4">
              <div className="col">
                <div className="ak-text-md fw-semibold">Total Tokens</div>
                <div className="fw-semibold">
                  {summary.totalChars?.toLocaleString()}
                </div>
              </div>
              <div className="col">
                <div className="ak-text-md fw-semibold">Limits</div>
                <div className="fw-semibold">
                  {summary.limit?.toLocaleString()}
                </div>
              </div>
            </div>

            {summary.breakdown.map((item, idx) => (
              <div key={idx} className="mb-3">
                <div className="d-flex justify-content-between ak-text-md fw-semibold">
                  <span className="text-secondary">
                    {item.type} ({item.chars?.toLocaleString()} chars)
                  </span>
                  <span>{item.value}%</span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    role="progressbar"
                    style={{ width: `${item.value}%` }}
                  />
                </div>
              </div>
            ))}

            {!summary.isOverLimit && summary.usedPercent >= 80 && (
              <div className="text-center small mb-2">
                Almost there! You’re close to the website training limit.
              </div>
            )}

            {summary.usedPercent === 100 && !summary.isOverLimit && (
              <div className="text-success text-center small mb-2">
                Website training limit reached 🎉
              </div>
            )}
            {summary.isOverLimit && (
              <div className="alert alert-warning py-2 mb-3 text-center">
                ⚠️ Character limit exceeded by{" "}
                <strong>{summary.overByChars?.toLocaleString()}</strong>{" "}
                characters.
                <div className="small mt-1">
                  New uploads are allowed but won’t improve training quality.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 🆕 Summary Modal */}
      {showSummaryModal && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div
            className="p-4 rounded-3 text-start position-relative"
            style={{
              backgroundColor: "#ffffff",
              width: "90%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <img
              src={crossIcon}
              alt="Close"
              className="custom-close-icon"
              onClick={() => setShowSummaryModal(false)}
            />

            <p className="fw-bold mt-2 ps-3 mb-3">
              Summaries from uploaded Documents
            </p>

            {summaryDataList.length === 0 ? (
              <p>No summaries available.</p>
            ) : (
              summaryDataList.map((summary, index) => (
                <div key={index} className="rounded-3 p-3 mb-3 bg-light">
                  <div className="mb-2 d-flex align-items-center gap-3">
                    {summary.type === "file" ? (
                      <img src={fileIcon} alt="file" width={20} height={20} />
                    ) : (
                      <img src={linkIcon} alt="link" width={20} height={20} />
                    )}
                    <div>
                      <div className=" text-truncate">
                        {summary.name || "Untitled"}
                      </div>
                    </div>
                  </div>

                  <div className="position-relative">
                    <textarea
                      className="form-control"
                      rows={5}
                      value={summary.summary || "No summary available."}
                      readOnly
                    />
                    <button
                      className="btn btn-link position-absolute text-white end-0 mt-1 bg-dark me-2"
                      onClick={() =>
                        regenerateSummary(summary?.approval_request_id, index)
                      }
                      disabled={loadingIndex === index} // 🚫 prevent double click
                    >
                      {loadingIndex === index ? (
                        <span className="spinner-border spinner-border-sm text-light" />
                      ) : (
                        <BsArrowRepeat size={18} />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}

            <div className="text-center mt-3">
              <button
                className="btn bg-dark text-white w-25"
                onClick={() => setShowSummaryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default TrainingWebsite;
